import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { apiClient, type MetaConfig } from '../utils/api';
import { useTelegram } from '../hooks/useTelegram';

const expenseSchema = z.object({
  selectedCategory: z.string().min(1, 'Подкатегория обязательна'),
  selectedTransaction: z.string().optional(),
  customTransactionName: z.string().optional(),
  customCategory: z.string().optional(),
  amount: z.number({ required_error: 'Сумма обязательна' }).min(0.01, 'Сумма должна быть больше 0'),
  fee: z.number().default(0),
  currency: z.string().min(1, 'Валюта обязательна'),
  account: z.string().min(1, 'Счет обязателен'),
  executor: z.string().min(1, 'Исполнитель обязателен'),
  transaction_time: z.string().min(1, 'Время транзакции обязательно'),
  note: z.string().optional(),
});

type ExpenseFormData = z.infer<typeof expenseSchema>;

interface Props {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const expenseCategories = [
  {
    id: 'top-management',
    name: 'Топ менеджмент',
    icon: '👑',
    category: 'топ менеджмент',
    transactions: [
      'зп алекс',
      'зп вова'
    ]
  },
  {
    id: 'salary',
    name: 'ЗП',
    icon: '👥',
    category: 'зп',
    transactions: [
      'зп настя',
      'зп кейт',
      'зп эдик (картинки)',
      'зп ssunelly',
      'зп дроидека',
      'зп мигато',
      'зп эвил',
      'зп альтернатив',
      'зп билдеры',
      'зп мирор'
    ]
  },
  {
    id: 'development',
    name: 'Разработка',
    icon: '💻',
    category: 'разработка',
    transactions: [
      'зп cororo',
      'зп голес',
      'зп дарт',
      'зп апопус',
      'зп марк',
      'зп кор',
      'зп дроидека dev'
    ]
  },
  {
    id: 'hosting',
    name: 'Хостинг',
    icon: '🌐',
    category: 'хостинг',
    transactions: [
      'Hetzner хосты',
      'OVH Roma',
      'Ovh хосты',
      'OVH Lokha',
      'Aaeza хосты'
    ]
  },
  {
    id: 'software',
    name: 'ПО',
    icon: '🔧',
    category: 'ПО',
    transactions: [
      'proxy check',
      'Подписка на клауд флаер'
    ]
  },
  {
    id: 'marketing',
    name: 'Маркетинг',
    icon: '📢',
    category: 'маркетинг',
    transactions: [
      'Маркетинг Mineland',
      'Дайджест',
      'Ютуберы',
      'Голосования'
    ]
  },
  {
    id: 'custom',
    name: 'Кастомная транзакция',
    icon: '📝',
    category: '',
    transactions: []
  }
];

export function ExpenseForm({ onSuccess, onCancel }: Props) {
  const [meta, setMeta] = useState<MetaConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { tg } = useTelegram();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<ExpenseFormData>({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      selectedCategory: '',
      selectedTransaction: '',
      customTransactionName: '',
      customCategory: '',
      amount: 0,
      fee: 0,
      currency: 'USD',
      account: '',
      executor: '',
      transaction_time: dayjs().format('YYYY-MM-DDTHH:mm'),
      note: '',
    },
  });

  const watchedSelectedCategory = watch('selectedCategory');
  const watchedSelectedTransaction = watch('selectedTransaction');
  const watchedAmount = watch('amount');
  const watchedFee = watch('fee');

  useEffect(() => {
    loadMeta();
  }, []);

  useEffect(() => {
    if (tg) {
      const buttonText = watchedSelectedTransaction ||
        (watchedSelectedCategory === 'custom' && watch('customTransactionName')) ||
        'Создать выплату';
      tg.MainButton.setText(buttonText);
      tg.MainButton.onClick(handleMainButtonClick);
      tg.MainButton.show();

      return () => {
        tg.MainButton.offClick(handleMainButtonClick);
        tg.MainButton.hide();
      };
    }
  }, [tg, watchedSelectedTransaction, watchedSelectedCategory, watch('customTransactionName')]);

  const loadMeta = async () => {
    try {
      const data = await apiClient.getMeta();
      setMeta(data);
    } catch (error) {
      console.error('Failed to load meta:', error);
    }
  };

  const handleMainButtonClick = () => {
    handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: ExpenseFormData) => {
    setIsLoading(true);
    try {
      const selectedCategoryData = expenseCategories.find(c => c.id === data.selectedCategory);

      let transactionName: string;
      let categoryName: string;

      if (data.selectedCategory === 'custom') {
        // Кастомная транзакция
        transactionName = data.customTransactionName || 'Кастомная выплата';
        categoryName = data.customCategory || 'прочие расходы';
      } else if (data.selectedTransaction) {
        // Предустановленная транзакция
        transactionName = data.selectedTransaction;
        categoryName = selectedCategoryData?.category || 'прочие расходы';
      } else {
        // Категория выбрана, но транзакция не выбрана
        transactionName = selectedCategoryData?.name || 'Выплата';
        categoryName = selectedCategoryData?.category || 'прочие расходы';
      }

      const transactionData = {
        name: transactionName,
        money: -Math.abs(data.amount), // Всегда отрицательное число для расхода
        fee: data.fee,
        currency: data.currency,
        account: data.account,
        executor: data.executor,
        transaction_time: data.transaction_time,
        destination_time: data.transaction_time,
        category: categoryName,
        payment: '',
        note: data.note || '',
      };

      await apiClient.createTransaction(transactionData);

      // Показываем анимацию успеха
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        reset();
        onSuccess?.();
      }, 2000);

    } catch (error) {
      console.error('Error saving transaction:', error);
      alert(`Ошибка: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!meta) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-telegram-hint">Загрузка...</p>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="p-6 text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <span className="text-2xl">✅</span>
          </div>
          <h3 className="text-xl font-semibold text-red-600 mb-2">Выплата создана!</h3>
          <p className="text-telegram-hint">Расходная транзакция успешно добавлена</p>
        </div>
      </div>
    );
  }

  // Рассчитываем итоговую сумму с комиссией
  const totalAmount = (watchedAmount || 0) + (watchedFee || 0);

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <span className="text-xl">💸</span>
        </div>
        <h2 className="text-xl font-bold text-telegram-text mb-1">Новая выплата</h2>
        <p className="text-sm text-telegram-hint">Расходная операция</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        {/* Выбор подкатегории */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-3">
            Подкатегория расхода
          </label>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {expenseCategories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => {
                  setValue('selectedCategory', category.id);
                  setValue('selectedTransaction', '');
                }}
                className={`
                  p-4 rounded-xl border-2 transition-all duration-200
                  ${watchedSelectedCategory === category.id
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                `}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">{category.icon}</div>
                  <div className={`text-sm font-medium ${
                    watchedSelectedCategory === category.id ? 'text-red-700' : 'text-gray-700'
                  }`}>
                    {category.name}
                  </div>
                  {category.transactions.length > 0 && (
                    <div className={`text-xs mt-1 ${
                      watchedSelectedCategory === category.id ? 'text-red-600' : 'text-gray-500'
                    }`}>
                      {category.transactions.length} вариантов
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
          {errors.selectedCategory && <p className="text-red-500 text-xs mt-2">{errors.selectedCategory.message}</p>}
        </div>

        {/* Выбор конкретной транзакции */}
        {watchedSelectedCategory && watchedSelectedCategory !== 'custom' && (
          <div className="animate-fadeIn">
            <label className="block text-sm font-medium text-telegram-text mb-3">
              Выберите транзакцию
            </label>
            {(() => {
              const selectedCategoryData = expenseCategories.find(c => c.id === watchedSelectedCategory);
              return selectedCategoryData?.transactions.length ? (
                <div className="grid grid-cols-1 gap-2">
                  {selectedCategoryData.transactions.map((transaction) => (
                    <button
                      key={transaction}
                      type="button"
                      onClick={() => setValue('selectedTransaction', transaction)}
                      className={`
                        p-3 rounded-lg border transition-all duration-200 text-left
                        ${watchedSelectedTransaction === transaction
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                        }
                      `}
                    >
                      {transaction}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm italic">Нет предустановленных транзакций для этой категории</p>
              );
            })()}
          </div>
        )}

        {/* Кастомная транзакция */}
        {watchedSelectedCategory === 'custom' && (
          <div className="animate-fadeIn space-y-4">
            <div>
              <label className="block text-sm font-medium text-telegram-text mb-2">
                Название транзакции
              </label>
              <input
                {...register('customTransactionName')}
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                placeholder="Введите название транзакции"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-telegram-text mb-2">
                Категория
              </label>
              <input
                {...register('customCategory')}
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                placeholder="Введите категорию"
              />
            </div>
          </div>
        )}


        {/* Сумма */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Сумма расхода
          </label>
          <div className="relative">
            <input
              {...register('amount', { valueAsNumber: true })}
              type="number"
              step="0.01"
              min="0"
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-lg font-semibold"
              placeholder="0.00"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-600 font-medium">
              {watch('currency')}
            </div>
          </div>
          {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount.message}</p>}
        </div>

        {/* Комиссия */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Комиссия <span className="text-gray-400">(необязательно)</span>
          </label>
          <div className="relative">
            <input
              {...register('fee', { valueAsNumber: true })}
              type="number"
              step="0.01"
              min="0"
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              placeholder="0.00"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
              {watch('currency')}
            </div>
          </div>
          <p className="text-xs text-telegram-hint mt-1">
            Дополнительные расходы (банковские комиссии и т.д.)
          </p>
        </div>

        {/* Предпросмотр транзакции */}
        {(watchedSelectedTransaction || (watchedSelectedCategory === 'custom' && watch('customTransactionName'))) && totalAmount > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <h4 className="text-sm font-medium text-red-700 mb-3">Предпросмотр транзакции:</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-red-600">Название:</span>
                <span className="text-sm font-medium text-red-700">
                  {watchedSelectedTransaction || watch('customTransactionName')}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-red-600">Категория:</span>
                <span className="text-sm font-medium text-red-700">
                  {watchedSelectedCategory === 'custom'
                    ? (watch('customCategory') || 'прочие расходы')
                    : expenseCategories.find(c => c.id === watchedSelectedCategory)?.category || 'прочие расходы'
                  }
                </span>
              </div>
              <div className="border-t border-red-200 pt-2 flex justify-between items-center">
                <span className="text-sm font-medium text-red-700">Итого к списанию:</span>
                <span className="text-lg font-bold text-red-700">
                  -{totalAmount.toFixed(2)} {watch('currency')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Валюта */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Валюта
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {meta.currencies.map((currency) => (
              <button
                key={currency}
                type="button"
                onClick={() => setValue('currency', currency)}
                className={`
                  p-3 rounded-lg border transition-all duration-200 font-medium
                  ${watch('currency') === currency
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {currency}
              </button>
            ))}
          </div>
          {errors.currency && <p className="text-red-500 text-xs mt-1">{errors.currency.message}</p>}
        </div>

        {/* Счет списания */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Счет для списания
          </label>
          <select
            {...register('account')}
            className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
          >
            <option value="">Выберите счет</option>
            {meta.accounts.map((account) => (
              <option key={account} value={account}>
                {account}
              </option>
            ))}
          </select>
          {errors.account && <p className="text-red-500 text-xs mt-1">{errors.account.message}</p>}
        </div>

        {/* Исполнитель */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Исполнитель операции
          </label>
          <select
            {...register('executor')}
            className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
          >
            <option value="">Выберите исполнителя</option>
            {meta.executors.map((executor) => (
              <option key={executor} value={executor}>
                {executor}
              </option>
            ))}
          </select>
          {errors.executor && <p className="text-red-500 text-xs mt-1">{errors.executor.message}</p>}
        </div>

        {/* Время */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Время операции
          </label>
          <input
            {...register('transaction_time')}
            type="datetime-local"
            className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
          />
          {errors.transaction_time && (
            <p className="text-red-500 text-xs mt-1">{errors.transaction_time.message}</p>
          )}
        </div>

        {/* Примечание */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Примечание <span className="text-gray-400">(необязательно)</span>
          </label>
          <textarea
            {...register('note')}
            className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
            rows={3}
            placeholder="Дополнительная информация о выплате"
          />
        </div>

        {/* Кнопки */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-red-500 text-white py-4 px-6 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-red-600 active:scale-95"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Создание...
              </div>
            ) : (
              `💸 ${watchedSelectedTransaction || (watchedSelectedCategory === 'custom' && watch('customTransactionName')) || 'Создать выплату'}`
            )}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-4 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 active:scale-95 transition-all"
            >
              Отмена
            </button>
          )}
        </div>
      </form>
    </div>
  );
}